import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import OptionTypeModal from './OptionTypeModal';
import {
    Box, Typography, Tooltip, Switch, FormControlLabel, TextField, Stack, Chip,
    Button, IconButton, InputAdornment, Alert,
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import { Delete, Undo, Add, Edit, Build } from "@mui/icons-material";
import ImageUploadField from './ImageUploadField';
import { v4 as uuidv4 } from 'uuid';
import QRCodeButton from './QRCodeButton';
import VariantComponentsModal from './VariantComponentsModal';
import VariantCombineModal from './VariantCombineModal';
const ProductVariantsTable = ({ hasLimit, variants, setVariants, control, optionTypes, setOptionTypes }) => {
    const { showSnackbar } = useSnackbar();
    const [editingOption, setEditingOption] = useState(null);
    const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseFilter, setWarehouseFilter] = useState([]);
    const [previewUrls, setPreviewUrls] = useState({});
    const [componentsModalOpen, setComponentsModalOpen] = useState(false);
    const [combineModalOpen, setCombineModalOpen] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);

    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const res = await axiosInstance.get('/warehouse', { params: { activeOnly: true } });
                setWarehouses(res.data);
                // 空チェック
                if (!res.data || res.data.length === 0) {
                    alert('倉庫の基本設定がありません。「設定>倉庫設定」から登録できます。');
                }
            } catch (err) {
                if (process.env.NODE_ENV === 'development') console.error(err);
            }
        };

        fetchWarehouses();
    }, []);
    // variantsを受け取ったときに、optionTypesを抽出して初期化
    useEffect(() => {
        if (!variants || variants.length === 0) return;

        const foundTypes = new Set();
        variants.forEach(v => {
            Object.keys(v.option_combination || {}).forEach(t => foundTypes.add(t));
        });

        setOptionTypes(prev => {
            const map = new Map(prev.map(o => [o.type, o]));

            foundTypes.forEach(type => {
                if (!map.has(type)) {
                    map.set(type, { type, deleted: false });
                }
            });

            return Array.from(map.values());
        });
    }, [variants]);
    useEffect(() => {
        return () => {
            Object.values(previewUrls).forEach(url => URL.revokeObjectURL(url));
        };
    }, [previewUrls]);

    // バリエーション追加
    const handleAddVariant = () => {
        // warehouses 配列がある前提
        const inventory = warehouses.map(w => ({
            warehouse_id: w.warehouse_id,
            warehouse_name: w.warehouse_name,
            logical_stock: '',
            baseline_stock: '',
            min_stock_level: ''
        }));
        const newVariant = {
            temp_id: uuidv4(),
            status: 'active',
            sku: '',
            price: '',
            min_order_quantity: 1,
            option_combination: Object.fromEntries(
                optionTypes
                    .filter(o => !o.deleted)
                    .map(o => [String(o.type), ''])
            ),
            weight: '',
            delivery_days: '',
            jan_code: '',
            sort_order: '',
            inventory
        };
        setVariants([...variants, newVariant]);
    };

    // 削除
    const handleRemoveVariant = async (variant) => {
        if (!window.confirm('このバリエーションを削除しますか？')) return;

        try {
            // 既存データのみAPI
            if (variant.variant_id) {
                await axiosInstance.delete(`/product/variant/${variant.variant_id}`);
            }

            // state更新（IDベースで削除）
            setVariants(prev =>
                prev.filter(v =>
                    v.variant_id
                        ? v.variant_id !== variant.variant_id
                        : v.temp_id !== variant.temp_id
                )
            );

        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };
    //画像アップロード
    const handleVariantImageChange = (vIdx, file, imgIdx = 0) => {
        if (!file) return;

        const variant = variants[vIdx];
        const tempId = variant.temp_id;

        // 既存のプレビュー URL があれば解放
        const prevUrl = previewUrls[`${tempId}-${imgIdx}`];
        if (prevUrl) URL.revokeObjectURL(prevUrl);

        const newUrl = URL.createObjectURL(file);

        // state 更新
        setPreviewUrls(prev => ({
            ...prev,
            [`${tempId}-${imgIdx}`]: newUrl,
        }));

        // variants にも保持（必要なら）
        const updated = [...variants];
        updated[vIdx].imageFiles = updated[vIdx].imageFiles || [];
        updated[vIdx].imageFiles[imgIdx] = {
            file,
            image_url: newUrl,
            image_order: imgIdx,
        };
        setVariants(updated);
    };
    const handleVariantImageRemove = (vIdx, imgIdx) => {
        const updated = [...variants];
        const variant = { ...updated[vIdx] };
        const imageFiles = variant.imageFiles ? [...variant.imageFiles] : [];

        if (imgIdx !== undefined && imageFiles.length > imgIdx) {
            const target = imageFiles[imgIdx];

            // プレビューURLがあるなら解放
            if (target?.image_url) {
                URL.revokeObjectURL(target.image_url);
            }

            if (target?.isExisting) {
                imageFiles[imgIdx] = { ...target, willDelete: true };
            } else {
                // 新規アップロード画像なら配列から削除
                imageFiles[imgIdx] = null;
            }
        }

        variant.imageFiles = imageFiles;
        updated[vIdx] = variant;
        setVariants(updated);
    };

    const handleDeleteOptionType = (deleteType) => {
        // variants からは完全削除
        setVariants(prev =>
            prev.map(v => {
                const newCombo = { ...v.option_combination };
                delete newCombo[deleteType];
                return { ...v, option_combination: newCombo };
            })
        );

        // optionTypes は論理削除
        setOptionTypes(prev =>
            prev.map(o =>
                o.type === deleteType
                    ? { ...o, deleted: true }
                    : o
            )
        );
    };

    return (
        <>
            {/* 追加・編集モーダル */}
            <OptionTypeModal
                open={isOptionModalOpen}
                editingType={editingOption}
                onAdd={(newType) => {
                    newType = String(newType);
                    if (optionTypes.some(o => o.type === newType)) return;

                    setOptionTypes(prev => [
                        ...prev,
                        { type: newType, deleted: false }
                    ]);
                    const updatedVariants = variants.map(v => ({
                        ...v,
                        option_combination: {
                            ...(v.option_combination && !Array.isArray(v.option_combination)
                                ? v.option_combination
                                : {}),
                            [String(newType)]: ''
                        }
                    }));
                    setVariants(updatedVariants);
                    setIsOptionModalOpen(false);
                }}
                onSave={(oldType, newType) => {
                    oldType = String(oldType);
                    newType = String(newType);
                    // 1. optionTypes を更新（object前提）
                    const updatedOptionTypes = optionTypes.map(o =>
                        o.type === oldType
                            ? { ...o, type: newType }
                            : o
                    );
                    setOptionTypes(updatedOptionTypes);

                    // 2. variants 側の option_combination を作り直す
                    const updatedVariants = variants.map(v => {
                        const oldCombo =
                            v.option_combination && !Array.isArray(v.option_combination)
                                ? v.option_combination
                                : {};

                        const newCombo = {};
                        const oldKey = String(oldType);
                        const newKey = String(newType);

                        Object.keys(oldCombo).forEach(key => {
                            if (key !== oldKey) {
                                newCombo[String(key)] = oldCombo[key];
                            }
                        });

                        newCombo[newKey] = oldCombo[oldKey] ?? '';

                        return { ...v, option_combination: newCombo };
                    });
                    setVariants(updatedVariants);
                    setIsOptionModalOpen(false);
                }}

                onCloseModal={() => {
                    setEditingOption(null);
                    setIsOptionModalOpen(false);
                }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems='center' justifyContent='end' sx={{ mb: 2 }}>
                <Typography variant="caption">倉庫で絞り込み</Typography>
                {warehouses.map((w) => (
                    <Chip
                        key={w.warehouse_id}
                        label={w.warehouse_name}
                        size='medium'
                        clickable
                        color={warehouseFilter.includes(w.warehouse_id) ? 'primary' : 'default'}
                        onClick={() => {
                            const warehouseId = w.warehouse_id;
                            const newFilter = warehouseFilter.includes(warehouseId)
                                ? warehouseFilter.filter(id => id !== warehouseId)
                                : [...warehouseFilter, warehouseId];

                            setWarehouseFilter(newFilter);
                        }}
                        sx={{ mb: 1 }}
                    />
                ))}
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }} style={{ maxHeight: '90vh' }}>
                <Table size="small" stickyHeader aria-label="sticky table" sx={{ width: 'max-content', minWidth: '100%' }}>
                    <TableHead>
                        <TableRow>
                            {hasLimit('ec') && (<TableCell>公開</TableCell>)}
                            <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>品番</TableCell>
                            {hasLimit('ec') && (<TableCell>画像</TableCell>)}
                            {optionTypes.map(({ type, deleted }) => (
                                <TableCell
                                    key={type}
                                    sx={{
                                        opacity: deleted ? 0.4 : 1,
                                        backgroundColor: deleted ? '#f5f5f5' : '#fff',
                                    }}
                                >
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        {type}

                                        <IconButton
                                            size="small"
                                            disabled={deleted}
                                            onClick={() => {
                                                setEditingOption(type);
                                                setIsOptionModalOpen(true);
                                            }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>

                                        {!deleted && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteOptionType(type)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </TableCell>
                            ))}
                            <TableCell sx={{ width: '10em' }}>価格</TableCell>
                            {/*<TableCell sx={{ width: '8em' }}>重量</TableCell>*/}
                            <TableCell sx={{ width: '9em' }}>最低注文数</TableCell>
                            <TableCell sx={{ width: '10em' }}>発送日数</TableCell>
                            <TableCell sx={{ width: '12em' }}>JAN</TableCell>
                            <TableCell sx={{ width: '9em' }}>在庫数</TableCell>
                            <TableCell sx={{ width: '9em' }}>基準在庫</TableCell>
                            <TableCell sx={{ width: '9em' }}>最低在庫</TableCell>
                            <TableCell sx={{ width: '9em' }}>並び順</TableCell>
                            <TableCell>操作</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {variants.map((variant, vIdx) => (
                            <TableRow
                                key={variant.variant_id ?? variant.temp_id}
                            >
                                {hasLimit('ec') && (
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Tooltip title={variant.status === "active" ? "販売中" : "非公開"}>
                                                    <Switch
                                                        checked={variant.status === "active"}
                                                        onChange={(e) => {
                                                            const updated = [...variants];
                                                            updated[vIdx].status = e.target.checked ? "active" : "pending";
                                                            setVariants(updated);
                                                        }}
                                                        color="primary"
                                                    />
                                                </Tooltip>
                                            }
                                        />
                                        {hasLimit('max_picking_devices') && variant.variant_id && (
                                            <QRCodeButton variantId={variant.variant_id} />
                                        )}
                                    </TableCell>
                                )}
                                <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1 }}>
                                    <Controller
                                        name={`variants.${vIdx}.sku`}
                                        control={control}
                                        defaultValue={variant.sku || ''}
                                        rules={{
                                            validate: value => {
                                                if (variants[vIdx].willDelete) return true;
                                                if (!value?.trim()) return '品番は必須です';
                                                if (value.length > 100) return '品番は100文字以内で入力してください';
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                error={!!error}
                                                helperText={error?.message}
                                                inputProps={{
                                                    maxLength: 100,
                                                }}
                                                onChange={e => {
                                                    field.onChange(e);
                                                    const updated = [...variants];
                                                    updated[vIdx].sku = e.target.value;
                                                    setVariants(updated);
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                                {hasLimit('ec') && (
                                    <TableCell>
                                        <ImageUploadField
                                            imageFile={variant.imageFiles?.[0]}
                                            onChange={e => handleVariantImageChange(vIdx, e.target.files[0], 0)}
                                            onRemove={() => handleVariantImageRemove(vIdx, 0)}
                                            size={80}
                                        />
                                    </TableCell>
                                )}
                                {optionTypes.map(o => (
                                    <TableCell key={o.type}>
                                        <Controller
                                            name={`variants.${vIdx}.option_combination.${o.type}`}
                                            control={control}
                                            defaultValue={variant.option_combination?.[o.type] ?? ''}
                                            rules={{
                                                validate: value => {
                                                    if (variants[vIdx].willDelete || o.deleted) return true;
                                                    if (!value?.trim()) return `${o.type}は必須です`;
                                                    if (value.length > 20) return `${o.type}は20文字以内で入力してください`;
                                                    return true;
                                                }
                                            }}
                                            render={({ field, fieldState: { error } }) => (
                                                <TextField
                                                    {...field}
                                                    disabled={o.deleted}
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    inputProps={{
                                                        maxLength: 20,
                                                    }}
                                                    onChange={e => {
                                                        field.onChange(e);
                                                        const updated = [...variants];
                                                        updated[vIdx].option_combination = {
                                                            ...updated[vIdx].option_combination,
                                                            [o.type]: e.target.value
                                                        };
                                                        setVariants(updated);
                                                    }}
                                                />
                                            )}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell>
                                    <Controller
                                        name={`variants.${vIdx}.price`}
                                        control={control}
                                        defaultValue={variant.price ?? ''}
                                        rules={{
                                            validate: value => {
                                                if (value === '') return true;
                                                if (value.length > 8) return '8桁以内で入力してください';
                                                if (!/^\d*$/.test(value)) return '数字のみ入力可能です';
                                                if (parseFloat(value) < 0) return '0以上の数字を入力してください';
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                type="text"
                                                error={!!error}
                                                helperText={error?.message}
                                                inputProps={{ maxLength: 8 }}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    field.onChange(val);
                                                    const updated = [...variants];
                                                    updated[vIdx].price = val === '' ? '' : parseFloat(val);
                                                    setVariants(updated);
                                                }}
                                                InputProps={{
                                                    min: 0,
                                                    endAdornment: <InputAdornment position="end">円</InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                                {/*
                                    <TableCell>
                                        <Controller
                                            name={`variants.${vIdx}.weight`}
                                            control={control}
                                            defaultValue={variant.weight ?? ''}
                                            rules={{
                                                validate: value => {
                                                    if (value === '') return true; // 空はOK
                                                    if (!/^\d*\.?\d*$/.test(value)) return '数字のみ入力可能です';
                                                    if (parseFloat(value) < 0) return '0以上の数字を入力してください';
                                                    return true;
                                                }
                                            }}
                                            render={({ field, fieldState: { error } }) => (
                                                <TextField
                                                    {...field}
                                                    type="text"
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        field.onChange(val);
                                                        const updated = [...variants];
                                                        updated[vIdx].weight = val === '' ? '' : parseFloat(val);
                                                        setVariants(updated);
                                                    }}
                                                    inputProps={{ min: 0 }}
                                                />
                                            )}
                                        />
                                    </TableCell>
                                */}
                                <TableCell>
                                    <Controller
                                        name={`variants.${vIdx}.min_order_quantity`}
                                        control={control}
                                        defaultValue={variant.min_order_quantity ?? 1}
                                        rules={{
                                            validate: value => {
                                                if (value === '') return true;
                                                if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                if (parseInt(value) < 1) return '1以上の数字を入力してください';
                                                if (value.length > 6) return '6桁以内で入力してください';
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                type="text"
                                                error={!!error}
                                                helperText={error?.message}
                                                inputProps={{ maxLength: 6 }}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    field.onChange(val);
                                                    const updated = [...variants];
                                                    updated[vIdx].min_order_quantity = val === '' ? '' : parseInt(val);
                                                    setVariants(updated);
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Controller
                                        name={`variants.${vIdx}.delivery_days`}
                                        control={control}
                                        defaultValue={variant.delivery_days ?? ''}
                                        rules={{
                                            validate: value => {
                                                if (value === '') return true;
                                                if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                if (parseInt(value) < 0) return '0以上の数字を入力してください';
                                                if (value.length > 5) return '5桁以内で入力してください';
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                type="text"
                                                error={!!error}
                                                helperText={error?.message}
                                                inputProps={{ maxLength: 5 }}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    field.onChange(val);
                                                    const updated = [...variants];
                                                    updated[vIdx].delivery_days = val === '' ? '' : parseInt(val);
                                                    setVariants(updated);
                                                }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">日</InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Controller
                                        name={`variants.${vIdx}.jan_code`}
                                        control={control}
                                        defaultValue={variant.jan_code ?? ''}
                                        rules={{
                                            validate: value => {
                                                if (!value) return true; // 空はOK
                                                if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                if (value.length > 13) return '13桁以内で入力してください';
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                type="text"
                                                error={!!error}
                                                helperText={error?.message}
                                                inputProps={{ maxLength: 13 }}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    field.onChange(val);
                                                    const updated = [...variants];
                                                    updated[vIdx].jan_code = val;
                                                    setVariants(updated);
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    {variant.inventory
                                        ?.filter(inv => warehouseFilter.length === 0 || warehouseFilter.includes(inv.warehouse_id))
                                        .map((inv, wIdx) => (
                                            <Box key={`logical-${inv.warehouse_id}`} sx={{ my: 1.2 }}>
                                                <Controller
                                                    name={`variants.${vIdx}.inventory.${wIdx}.logical_stock`}
                                                    control={control}
                                                    defaultValue={inv.logical_stock ?? ''}
                                                    rules={{
                                                        validate: value => {
                                                            if (value === '') return true;
                                                            if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                            if (Number(value) < 0) return '0以上の数字を入力してください';
                                                            if (value.length > 9) return '9桁以内で入力してください';
                                                            return true;
                                                        }
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            label={inv.warehouse_name}
                                                            type="text"
                                                            error={!!error}
                                                            helperText={error?.message}
                                                            inputProps={{ maxLength: 9 }}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                field.onChange(val);
                                                                const updated = [...variants];
                                                                updated[vIdx].inventory[wIdx] = {
                                                                    ...updated[vIdx].inventory[wIdx],
                                                                    logical_stock: val === '' ? '' : Number(val)
                                                                };
                                                                setVariants(updated);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>

                                        ))}
                                </TableCell>
                                <TableCell>
                                    {variant.inventory
                                        ?.filter(inv => warehouseFilter.length === 0 || warehouseFilter.includes(inv.warehouse_id))
                                        .map((inv, wIdx) => (
                                            <Box key={`baseline-${inv.warehouse_id}`} sx={{ my: 1.2 }}>
                                                <Controller
                                                    name={`variants.${vIdx}.inventory.${wIdx}.baseline_stock`}
                                                    control={control}
                                                    defaultValue={inv.baseline_stock ?? ''}
                                                    rules={{
                                                        validate: value => {
                                                            if (value === '') return true;
                                                            if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                            if (Number(value) < 0) return '0以上の数字を入力してください';
                                                            if (value.length > 9) return '9桁以内で入力してください';
                                                            return true;
                                                        }
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            label={inv.warehouse_name}
                                                            type="text"
                                                            error={!!error}
                                                            helperText={error?.message}
                                                            inputProps={{ maxLength: 9 }}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                field.onChange(val);

                                                                const updated = [...variants];
                                                                updated[vIdx].inventory[wIdx] = {
                                                                    ...updated[vIdx].inventory[wIdx],
                                                                    baseline_stock: val === '' ? '' : Number(val)
                                                                };
                                                                setVariants(updated);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        ))}
                                </TableCell>
                                <TableCell>
                                    {variant.inventory
                                        ?.filter(inv => warehouseFilter.length === 0 || warehouseFilter.includes(inv.warehouse_id))
                                        .map((inv, wIdx) => (
                                            <Box key={`min-stock-${inv.warehouse_id}`} sx={{ my: 1.2 }}>
                                                <Controller
                                                    name={`variants.${vIdx}.inventory.${wIdx}.min_stock_level`}
                                                    control={control}
                                                    defaultValue={inv.min_stock_level ?? ''}
                                                    rules={{
                                                        validate: value => {
                                                            if (value === '') return true;
                                                            if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                            if (Number(value) < 0) return '0以上の数字を入力してください';
                                                            if (value.length > 9) return '9桁以内で入力してください';
                                                            return true;
                                                        }
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            label={inv.warehouse_name}
                                                            type="text"
                                                            error={!!error}
                                                            helperText={error?.message}
                                                            inputProps={{ maxLength: 9 }}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                field.onChange(val);
                                                                const updated = [...variants];
                                                                updated[vIdx].inventory[wIdx] = {
                                                                    ...updated[vIdx].inventory[wIdx],
                                                                    min_stock_level: val === '' ? '' : Number(val)
                                                                };
                                                                setVariants(updated);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        ))}
                                </TableCell>
                                <TableCell>
                                    <Controller
                                        name={`variants.${vIdx}.sort_order`}
                                        control={control}
                                        defaultValue={variant.sort_order ?? vIdx}
                                        rules={{
                                            validate: value => {
                                                if (value === '') return true;
                                                if (!/^\d+$/.test(value)) return '数字のみ入力可能です';
                                                if (parseInt(value) < 0) return '0以上の数字を入力してください';
                                                if (value.length > 9) return '9桁以内で入力してください';
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                variant="outlined"
                                                type="text"
                                                error={!!error}
                                                helperText={error?.message}
                                                inputProps={{ maxLength: 9 }}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    field.onChange(val);
                                                    const updated = [...variants];
                                                    updated[vIdx].sort_order = val === '' ? '' : parseInt(val);
                                                    setVariants(updated);
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                                <TableCell>
                                    {/*
                                    <Tooltip title="セット商品内容">
                                        <IconButton
                                            onClick={() => {
                                                if (!variant.variant_id) {
                                                    alert("一度商品を保存してから構成を設定してください");
                                                    return;
                                                }
                                                setSelectedVariant(variant);
                                                setCombineModalOpen(true);
                                            }}
                                        >
                                            <Build />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="仕入れ商品構成">
                                        <IconButton
                                            onClick={() => {
                                                if (!variant.variant_id) {
                                                    alert("一度商品を保存してから構成を設定してください");
                                                    return;
                                                }
                                                setSelectedVariant(variant);
                                                setComponentsModalOpen(true);
                                            }}
                                        >
                                            <Build />
                                        </IconButton>
                                    </Tooltip>
                                     */}
                                    <Tooltip title="削除">
                                        <IconButton onClick={() => handleRemoveVariant(variant)}>
                                            <Delete />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className='button-row'>
                <Button
                    type="button"
                    startIcon={<Add />}
                    variant="outlined"
                    size="small"
                    onClick={handleAddVariant}
                >
                    バリエーション行を追加
                </Button>
                <Button
                    startIcon={<Add />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setEditingOption(null);
                        setIsOptionModalOpen(true);
                    }}
                >
                    オプション列を追加
                </Button>
            </div>
            <VariantCombineModal
                open={combineModalOpen}
                variant={selectedVariant}
                onClose={() => setCombineModalOpen(false)}
            />
            <VariantComponentsModal
                open={componentsModalOpen}
                variant={selectedVariant}
                onClose={() => setComponentsModalOpen(false)}
            />
        </>
    );
};

export default ProductVariantsTable;
